/**
 * SummaryDrawer — coquille du volet de synthèse (panneau latéral droit).
 *
 * `<aside>` NON-MODAL (jamais un `Sheet` : le focus-trap tuerait la navigation,
 * or on veut garder la fiche « sous les yeux » en travaillant ailleurs). Copié du
 * patron ancré de `HelpDrawer` (§docked). Chrome = croix seule (fermer =
 * dé-épingler ; pas d'épingle dans le volet — décision PO). Desktop uniquement.
 *
 * Cohabitation avec l'aide : quand le Help Drawer est ouvert, le volet est MASQUÉ
 * (`display:none`, état préservé) et réapparaît intact à la fermeture de l'aide.
 * L'action « Envoyer un email » réutilise le compositeur classique tracé
 * (`SearchEmailComposer`), piloté par un state local + `useEntity()`.
 */

import { useState } from 'react'
import { Loader2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ErrorBoundary } from '@/components/ErrorBoundary'
import { SearchDocumentQuickLook } from '@/components/search/SearchDocumentQuickLook'
import { SearchEmailComposer } from '@/components/search/SearchEmailComposer'
import { Button } from '@/components/ui/button'
import { useEntity } from '@/contexts/EntityContext'
import { useHelpContext } from '@/contexts/HelpContext'
import { useSummaryDrawer } from '@/contexts/SummaryDrawerContext.helpers'
import { useSummaryDetail } from '@/hooks/useSummaryDetail'
import { linkedEntityHref } from '@/lib/linkedEntity'
import { cn } from '@/lib/utils'
import { SummaryContent } from './SummaryContent'
import { SummarySkeleton } from './primitives'

export function SummaryDrawer() {
    const { t } = useTranslation()
    const { pinned, unpin, visible } = useSummaryDrawer()
    const help = useHelpContext()
    const { currentEntity } = useEntity()
    const navigate = useNavigate()
    const currentEntityId = currentEntity?.entityId ?? null

    const [composeEmail, setComposeEmail] = useState<{ email: string; name: string } | null>(null)
    // Lecteur plein écran d'une pièce — le MÊME que celui de la palette ⌘K. Une
    // fiche de document épinglée propose « Aperçu » en action première : sans
    // lecteur monté ici, le bouton serait retiré et le volet perdrait ce que la
    // palette offre, alors que les deux rendent désormais la même fiche.
    const [quickLookFileId, setQuickLookFileId] = useState<string | null>(null)

    const { data, isLoading, isError, isFetching, refetch } = useSummaryDetail(pinned)

    // Fermé (non épinglé) ou mobile → rien. Sur transition desktop→mobile, le
    // Context purge `pinned` (donc `visible` passe à false et on démonte).
    if (!visible || !pinned) return null

    const onOpen = () => {
        const href = linkedEntityHref(pinned.type, pinned.id)
        if (href) navigate(href)
    }
    const onNavigate = (href: string) => navigate(href)
    const onSendEmail = (target: { email: string; name: string }) => setComposeEmail(target)

    return (
        <aside
            aria-label={t('summaryDrawer.title', 'Synthèse')}
            className={cn(
                'fixed right-0 top-0 z-30 flex h-full w-[32rem] max-w-full flex-col',
                'border-l border-border bg-background shadow-md',
                'animate-in slide-in-from-right duration-300 ease-out motion-reduce:animate-none',
                // Masqué (état préservé) quand l'aide est ouverte — réapparaît intact.
                help.isOpen && 'hidden',
            )}
        >
            {/* Chrome — croix seule (fermer = dé-épingler). */}
            <div className="flex items-center justify-end border-b border-border bg-muted/40 px-3 py-2">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={unpin}
                    aria-label={t('summaryDrawer.close', 'Fermer la synthèse')}
                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                >
                    <X className="h-4 w-4" />
                </Button>
            </div>

            {/* Corps — région annoncée, scroll interne (jamais de scroll horizontal). */}
            <div
                role="region"
                aria-label={t('summaryDrawer.title', 'Synthèse')}
                aria-live="polite"
                aria-busy={isLoading}
                className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
            >
                {isLoading && !data ? (
                    <SummarySkeleton />
                ) : isError && !data ? (
                    <div className="flex flex-col items-center justify-center gap-3 p-8 text-center text-sm text-muted-foreground">
                        <p>{t('summaryDrawer.error', 'Impossible de charger la synthèse.')}</p>
                        <Button variant="outline" size="sm" onClick={() => refetch()}>
                            {t('common.retry', 'Réessayer')}
                        </Button>
                    </div>
                ) : data ? (
                    <ErrorBoundary
                        fallbackMessage={t('summaryDrawer.error', 'Impossible de charger la synthèse.')}
                    >
                        <SummaryContent
                            data={data}
                            currentEntityId={currentEntityId}
                            onOpen={onOpen}
                            onNavigate={onNavigate}
                            onSendEmail={onSendEmail}
                            onQuickLook={setQuickLookFileId}
                        />
                    </ErrorBoundary>
                ) : null}

                {/* Refetch en fond (données déjà présentes) → indicateur discret. */}
                {isFetching && data && (
                    <div className="flex items-center gap-2 px-5 pb-4 text-xs text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                        {t('summaryDrawer.loading', 'Actualisation…')}
                    </div>
                )}
            </div>

            {/* Lecteur « coup d'œil » — monté HORS du corps scrollable (il survit
                au changement de fiche épinglée). L'entité est celle de la fiche
                (cross-entités), à défaut l'entité courante. */}
            {quickLookFileId && data && (
                <SearchDocumentQuickLook
                    open
                    onOpenChange={(o) => {
                        if (!o) setQuickLookFileId(null)
                    }}
                    fileId={quickLookFileId}
                    filename={data.header.label}
                    mime={data.document?.mime ?? null}
                    entityId={data.entity?.id ?? currentEntityId ?? ''}
                />
            )}

            {/* Compositeur d'email (lazy) — réutilise le flux classique tracé. */}
            <SearchEmailComposer
                open={!!composeEmail}
                recipientEmail={composeEmail?.email ?? ''}
                entityId={currentEntityId ?? ''}
                onOpenChange={(o) => {
                    if (!o) setComposeEmail(null)
                }}
            />
        </aside>
    )
}

export default SummaryDrawer
